import React, { FC, useCallback, useEffect, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import Button from '@mui/material/Button';
import FormHelperText from '@mui/material/FormHelperText';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useFormik } from 'formik';
import isEqual from 'lodash/isEqual';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { CheckFilledIcon, InputMaskField, CloseIcon, Select } from 'src/shared/components';
import { TimeFormatEnums } from 'src/shared/enums';
import { useThunkDispatch } from 'src/shared/hooks';
import { useTranslate } from 'src/shared/providers';
import { RootState } from 'src/store/types';
import { fetchPreferences, updatePreferences, updateUserInfo } from 'src/store/user/user.actions';
import { getPhoneMask, getTimeFormatId } from 'src/store/user/user.selectors';

type UserPreferencesInputsType = { email: string; cellPhone: string };

interface Props {
  showButtons?: boolean;
  classes?: { wrapper?: string; input?: string; buttons?: string };
  onCancel?: () => void;
  onSubmit?: () => void;
}

export const UserPreferencesForm: FC<Props> = ({ showButtons, classes = {}, onCancel, onSubmit }) => {
  const { t } = useTranslate();
  const dispatch = useThunkDispatch();
  const { info } = useSelector((state: RootState) => state.user);
  const phoneMask = useSelector(getPhoneMask);
  const [timeFormat, setTimeFormat] = useState<TimeFormatEnums>(TimeFormatEnums.T_24_HOURS);
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const timeFormatId = useSelector(getTimeFormatId);

  useEffect(() => {
    setTimeFormat(timeFormatId);
  }, [timeFormatId]);

  const submitForm = async (values: UserPreferencesInputsType) => {
    try {
      if (!isEqual(values, { email: info?.email, cellPhone: info?.cellPhone })) {
        await dispatch(updateUserInfo({ ...values, cellPhone: values.cellPhone.replace(/\D/g, '') }));
      }

      if (timeFormatId !== timeFormat) {
        await dispatch(updatePreferences({ timeFormat }));
        await dispatch(fetchPreferences());
      }
      toast.info(t('settings.successYourUserPreferencesWereUpdatedSuccessfully'), { type: toast.TYPE.SUCCESS });

      if (onSubmit) {
        onSubmit();
      }
    } catch {
      toast.info(t('global.oopsAnErrorOccurredWhileProcessingYourRequest'), { type: toast.TYPE.ERROR });
    }
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string().email(t('settings.theInputIsNotAValidEmail')),
    cellPhone: Yup.string().test(
      'isCharactersLengthValid',
      t('settings.theCellPhoneMustHaveExactlyXCharacters', phoneMask.replace(/[- )(]/g, '').length),
      (val) => !val || val.replace(/\D/g, '').length === phoneMask.replace(/[- )(]/g, '').length,
    ),
  });

  const getTimeFormatValue = useCallback(
    (value: TimeFormatEnums): string => {
      switch (value) {
        case TimeFormatEnums.T_12_HOURS:
          return t('settings.12hoursamPM');
        case TimeFormatEnums.T_24_HOURS:
          return t('settings.24hours');
        case TimeFormatEnums.T_DECIMAL:
          return t('settings.24hoursDecimal');
        default:
          return '-';
      }
    },
    [t],
  );

  const timeFormats = [TimeFormatEnums.T_12_HOURS, TimeFormatEnums.T_24_HOURS].map((key) => {
    return { key, value: getTimeFormatValue(key) };
  });

  const { handleChange, isSubmitting, handleSubmit, dirty, values, touched, errors, resetForm, initialValues } =
    useFormik({
      enableReinitialize: true,
      initialValues: { email: info?.email || '', cellPhone: info?.cellPhone || '' },
      validationSchema,
      onSubmit: submitForm,
    });

  useEffect(() => {
    setIsDisabled(isSubmitting || !dirty || isEqual(initialValues, values));
  }, [isSubmitting, dirty, values]);

  const handleCancel = () => {
    resetForm();

    if (onCancel) {
      onCancel();
    }
  };

  const handleTimeFormat = (value: TimeFormatEnums) => {
    setTimeFormat(value);
    if (timeFormatId === value) {
      setIsDisabled(true);
    } else {
      setIsDisabled(false);
    }
  };

  return (
    <>
      <div className={classes.wrapper}>
        <div className={`mb-3 ${classes.input || ''}`}>
          <label>{t('settings.emailAddress')}</label>
          <div>
            <TextField
              className="mb-1"
              fullWidth
              variant="outlined"
              size="small"
              margin="normal"
              required
              id="email"
              name="email"
              autoComplete="email"
              value={values.email}
              disabled={!info}
              error={touched.email && !!errors.email}
              onInput={handleChange('email')}
            />
            {touched.email && !!errors.email && <FormHelperText error>{errors.email}</FormHelperText>}
          </div>
        </div>
        <div className={`${classes.input || ''}`}>
          <label>{t('settings.cellPhoneNumber')}</label>
          <div>
            <InputMaskField
              className="mb-1"
              fullWidth
              variant="outlined"
              size="small"
              margin="normal"
              required
              id="phone"
              name="phone"
              autoComplete="phone"
              disabled={!info}
              format={phoneMask}
              value={values.cellPhone}
              error={touched.cellPhone && !!errors.cellPhone}
              onChange={handleChange('cellPhone')}
            />
            {touched.cellPhone && !!errors.cellPhone && (
              <FormHelperText error className="mt-0">
                {errors.cellPhone}
              </FormHelperText>
            )}
          </div>
        </div>
        <div className={`${classes.input || ''}`}>
          <label style={{ marginTop: '15px' }}>{t('settings.timeFormat')}</label>
          <div>
            <Select
              id="time-format-dropdown"
              value={timeFormat}
              className="time-format-input"
              onChange={(e) => {
                handleTimeFormat(e.target.value);
              }}
            >
              {timeFormats.map(({ key, value }) => {
                return (
                  <MenuItem key={`tf-${key}`} value={key}>
                    {value}
                  </MenuItem>
                );
              })}
            </Select>
          </div>
        </div>
      </div>
      {(showButtons || (dirty && !isEqual(initialValues, values)) || timeFormatId !== timeFormat) && (
        <div className={`mt-4 d-flex align-items-center ${classes.buttons || ''}`}>
          <Button
            className="me-2"
            startIcon={<CloseIcon />}
            color="secondary"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            {t('global.cancel')}
          </Button>
          <LoadingButton
            startIcon={<CheckFilledIcon />}
            variant="contained"
            loadingPosition="start"
            onClick={handleSubmit as any}
            disabled={isDisabled}
            loading={isSubmitting}
          >
            {t('global.applyChanges')}
          </LoadingButton>
        </div>
      )}
    </>
  );
};
